const API_BASE = 'https://tool.estimateassistant.com';

/**
 * Subscription Client
 * Handles Stripe checkout and subscription status
 */

/**
 * Create a Stripe checkout session and redirect to checkout
 * @param {object} user - Clerk user object
 * @returns {Promise<void>}
 */
export async function createCheckoutSession(user) {
  if (!user || !user.id || !user.primaryEmailAddress?.emailAddress) {
    throw new Error('User information is required');
  }

  try {
    const response = await fetch(`${API_BASE}/api/subscription/create-checkout-session`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        userId: user.id,
        userEmail: user.primaryEmailAddress.emailAddress,
      }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to create checkout session' }));
      throw new Error(error.error || `HTTP ${response.status}`);
    }

    const data = await response.json();

    if (!data.url) {
      throw new Error('No checkout URL received');
    }

    // Redirect to Stripe checkout
    window.location.href = data.url;
  } catch (error) {
    console.error('Checkout session error:', error);
    throw error;
  }
}

/**
 * Get subscription status for a user
 * @param {string} userId - Clerk user ID
 * @returns {Promise<object>} - Subscription status object
 */
export async function getSubscriptionStatus(userId) {
  if (!userId) {
    throw new Error('User ID is required');
  }

  try {
    const response = await fetch(`${API_BASE}/api/subscription/status/${userId}`);

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to fetch subscription status' }));
      throw new Error(error.error || `HTTP ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Subscription status error:', error);
    throw error;
  }
}

/**
 * Check if user has an active subscription
 * @param {string} userId - Clerk user ID
 * @returns {Promise<boolean>} - True if user has active subscription
 */
export async function hasActiveSubscription(userId) {
  try {
    const status = await getSubscriptionStatus(userId);
    return status.subscribed === true || status.status === 'active';
  } catch (error) {
    console.error('Error checking subscription:', error);
    return false;
  }
}

/**
 * Create a Stripe customer portal session and redirect to portal
 * Allows users to manage subscription, update payment method, view invoices, and cancel
 * @param {object} user - Clerk user object
 * @returns {Promise<void>}
 */
export async function createPortalSession(user) {
  if (!user || !user.id) {
    throw new Error('User ID is required');
  }

  try {
    const response = await fetch(`${API_BASE}/api/subscription/create-portal-session`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        userId: user.id,
      }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to create portal session' }));
      throw new Error(error.error || `HTTP ${response.status}`);
    }

    const data = await response.json();

    if (!data.url) {
      throw new Error('No portal URL received');
    }

    // Redirect to Stripe customer portal
    window.location.href = data.url;
  } catch (error) {
    console.error('Portal session error:', error);
    throw error;
  }
}
